/**
 * Atribuição leve da landing — Fase 5 do redesign (13/09/2026).
 *
 * O roadmap pede a instrumentação "segmentada por mobile/desktop e por fonte
 * de tráfego". Isto cobre as duas dimensões sem introduzir um SDK novo:
 *
 *  - **Fonte de tráfego**: `utm_source`/`utm_medium`/`utm_campaign` da URL de
 *    entrada, mais o host do `document.referrer` (nunca a URL inteira — sem
 *    PII, mesma convenção de `lib/telemetry.ts`). Capturado UMA vez, na
 *    primeira renderização da `/` nesta aba, e guardado em `sessionStorage`:
 *    sem persistir, o clique num CTA (que já saiu da landing) e o
 *    `signup_completed` (na `/login`, depois de navegar) chegariam sem a
 *    fonte que trouxe a pessoa — exatamente o dado que o funil pede pra medir.
 *  - **Mobile/desktop**: `matchMedia` contra os mesmos 880px que o resto da
 *    landing usa como corte (`primitives.tsx`), lido no MOMENTO do evento —
 *    não precisa persistir, o aparelho não muda no meio da sessão.
 */

const STORAGE_KEY = "livra_landing_attribution";
const DESKTOP_QUERY = "(min-width: 880px)";

export interface LandingAttribution {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  referrer_host?: string;
}

function readSessionStorage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/**
 * Chamada uma vez, na montagem de `Landing.tsx`. Primeiro toque vence: uma
 * segunda visita à `/` na mesma aba (voltar pelo navegador, por exemplo) não
 * sobrescreve a fonte original.
 */
export function captureLandingAttribution(): void {
  const storage = readSessionStorage();
  if (!storage) return;
  if (storage.getItem(STORAGE_KEY)) return;

  const params = new URLSearchParams(window.location.search);
  const attribution: LandingAttribution = {};

  const utmSource = params.get("utm_source");
  const utmMedium = params.get("utm_medium");
  const utmCampaign = params.get("utm_campaign");
  if (utmSource) attribution.utm_source = utmSource;
  if (utmMedium) attribution.utm_medium = utmMedium;
  if (utmCampaign) attribution.utm_campaign = utmCampaign;

  try {
    if (document.referrer) {
      attribution.referrer_host = new URL(document.referrer).hostname;
    }
  } catch {
    /* referrer malformado — segue sem ele */
  }

  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(attribution));
  } catch {
    /* storage indisponível — os eventos seguem sem atribuição */
  }
}

/** Lê a atribuição capturada nesta aba. `{}` se não houve captura (ou storage indisponível). */
export function getLandingAttribution(): LandingAttribution {
  const storage = readSessionStorage();
  if (!storage) return {};
  try {
    const raw = storage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as LandingAttribution) : {};
  } catch {
    return {};
  }
}

/** "mobile" | "desktop" no INSTANTE do evento — mesmo corte de `min-[880px]` da landing. */
export function getViewportBucket(): "mobile" | "desktop" {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return "mobile";
  return window.matchMedia(DESKTOP_QUERY).matches ? "desktop" : "mobile";
}
